import { memo, useMemo } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useServiceMetaQuery } from '@kc-console/context';

import { SCOPE_ORDER } from '../../../../constants';
import { useScopeLabel } from '../../../../hooks';
import ServiceListItem from './ServiceListItem';
import ServiceScopeGroup from './ServiceScopeGroup';

interface Props {
  collapsed?: boolean;
}

type ContainerProps = { collapsed?: boolean };

function ServiceListSection({ collapsed = false }: Props) {
  const scopeLabel = useScopeLabel();
  const { data: serviceMeta } = useServiceMetaQuery();
  const menus = useMemo(() => serviceMeta?.menus ?? [], [serviceMeta?.menus]);

  const { showScopeGroups, grouped } = useMemo(() => {
    const hasProject = menus.some(m => m.scope === 'project');
    const hasDomain = menus.some(m => m.scope === 'domain');

    return {
      showScopeGroups: hasProject && hasDomain,
      grouped: {
        unscope: menus.filter(m => m.scope === 'unscope'),
        project: menus.filter(m => m.scope === 'project'),
        domain: menus.filter(m => m.scope === 'domain'),
      },
    };
  }, [menus]);

  return (
    <ServiceListContainer collapsed={collapsed}>
      <nav aria-label="service-list">
        {showScopeGroups ? (
          <>
            {grouped.unscope.map(menu => (
              <ServiceListItem key={menu.path} menu={menu} collapsed={collapsed} />
            ))}
            {SCOPE_ORDER.map((scope, index) => {
              const items = grouped[scope];
              if (items.length === 0) return null;

              const isFirst = SCOPE_ORDER.slice(0, index).every(s => grouped[s].length === 0);

              return (
                <ServiceScopeGroup
                  key={scope}
                  label={scopeLabel[scope]}
                  menus={items}
                  collapsed={collapsed}
                  showDivider={!isFirst}
                />
              );
            })}
          </>
        ) : (
          menus.map(menu => <ServiceListItem key={menu.path} menu={menu} collapsed={collapsed} />)
        )}
      </nav>
    </ServiceListContainer>
  );
}

export default memo(ServiceListSection);

const ServiceListContainer = styled('ul', {
  shouldForwardProp: prop => prop !== 'collapsed',
})<ContainerProps>`
  display: flex;
  flex-direction: column;
  padding: 0;

  ${({ collapsed }) =>
    !collapsed &&
    css`
      flex: 1 1 0;
    `}
`;
